/**
 * PDFModal Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * A comprehensive PDF viewer modal with advanced features:
 * - PDF loading from multiple sources (URL, listItemId, title-based)
 * - Draggable and resizable modal window
 * - Fullscreen support with browser API integration
 * - Integrated Daire AI chat sidebar
 * - Keyboard navigation and shortcuts
 * - External URL support with security handling
 * - Loading states and error handling
 * - Member tools integration (open in new tab)
 * - Dynamic sizing and positioning
 * 
 * @param {string} title - PDF document title
 * @param {string} url - PDF URL or filename
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} allowFullscreen - Whether fullscreen mode is available
 * @param {boolean} allowMove - Whether the modal can be dragged
 * @param {boolean} showLinkinToolBar - Whether to show additional toolbar options
 * @param {Object} style - Additional CSS styles for the modal
 * @param {function} onBringToFront - Callback to bring modal to front
 * @param {string|number} width - Modal width (default: '800px')
 * @param {string|number} height - Modal height (default: '600px')
 * @param {string} listItemId - SharePoint list item ID for document fetching
 * @param {boolean} externalUrls - Whether to handle external URLs directly
 * @param {Object|null} consultationData - Consultation object containing details to display
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useState, useRef, useEffect, useContext } from 'react';
import Draggable from 'react-draggable';
import Tippy from '@tippyjs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import 'tippy.js/dist/tippy.css';
import './PDFModal.css';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { extractFileName, formatDate, normalizeText, truncateText } from '../utils/textUtils';
import DaireChat from '../components/Daire/DaireChat';
import { AuthContext } from '../components/Auth/AuthContext';

const MIN_SIDEBAR_WIDTH = 400;
const MAX_SIDEBAR_WIDTH = 600;

/**
 * PDFModal component for displaying PDF documents with advanced features
 * including AI chat integration and full document management capabilities
 */
function PDFModal({ title, url, show, onClose, allowFullscreen = false, allowMove = false, showLinkinToolBar = false, style, onBringToFront, width = '800px', height = '600px', listItemId, externalUrls = false, consultationData = null }) {
    const modalRef = useRef(null);
    const { user: [user] } = useContext(AuthContext);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isDaireOpen, setIsDaireOpen] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(MIN_SIDEBAR_WIDTH);
    const [isDraggingSidebar, setIsDraggingSidebar] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [hasBeenMoved, setHasBeenMoved] = useState(false);

    // Custom resize system state
    const [modalDimensions, setModalDimensions] = useState({
        width: typeof width === 'string' ? parseInt(width) : width,
        height: typeof height === 'string' ? parseInt(height) : height
    });
    const [isResizing, setIsResizing] = useState(false);
    const [showConsultationDetails, setShowConsultationDetails] = useState(false);
    const resizeState = useRef({
        isResizing: false,
        startX: 0,
        startY: 0,
        startWidth: 0,
        startHeight: 0,
        direction: ''
    });

    const handleSidebarResize = (e) => {
        e.preventDefault();
        setIsDraggingSidebar(true);
        const startX = e.clientX;
        const startWidth = sidebarWidth;

        const handleMouseMove = (e) => {
            const deltaX = startX - e.clientX;
            const newWidth = Math.min(Math.max(startWidth + deltaX, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
            setSidebarWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsDraggingSidebar(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    // Custom resize handlers with direct DOM manipulation
    const handleResizeStart = (direction) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (resizeState.current.isResizing) return; // Prevent multiple resize operations
        
        const modal = modalRef.current;
        if (!modal) return;
        
        // Set resize state
        resizeState.current.isResizing = true;
        resizeState.current.direction = direction;
        resizeState.current.startX = e.clientX;
        resizeState.current.startY = e.clientY;
        resizeState.current.startWidth = modalDimensions.width;
        resizeState.current.startHeight = modalDimensions.height;
        
        setIsResizing(true);
        
        // Prevent text selection and disable pointer events on content
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        document.body.style.pointerEvents = 'none';
        modal.style.pointerEvents = 'auto';

        const handleMouseMove = (e) => {
            if (!resizeState.current.isResizing) return;
            
            // Use requestAnimationFrame for smooth updates
            requestAnimationFrame(() => {
                if (!resizeState.current.isResizing) return;
                
                const deltaX = e.clientX - resizeState.current.startX;
                const deltaY = e.clientY - resizeState.current.startY;
                
                let newWidth = resizeState.current.startWidth;
                let newHeight = resizeState.current.startHeight;
                
                // Calculate new dimensions based on direction
                const minWidth = isDaireOpen ? 600 : 400;
                const maxWidth = window.innerWidth * 0.95;
                const maxHeight = window.innerHeight * 0.95;
                
                if (direction.includes('e')) newWidth = Math.max(minWidth, Math.min(maxWidth, resizeState.current.startWidth + deltaX));
                if (direction.includes('w')) newWidth = Math.max(minWidth, Math.min(maxWidth, resizeState.current.startWidth - deltaX));
                if (direction.includes('s')) newHeight = Math.max(300, Math.min(maxHeight, resizeState.current.startHeight + deltaY));
                if (direction.includes('n')) newHeight = Math.max(300, Math.min(maxHeight, resizeState.current.startHeight - deltaY));
                
                // Apply dimensions immediately to DOM
                const totalWidth = newWidth + (isDaireOpen ? sidebarWidth : 0);
                modal.style.width = `${totalWidth}px`;
                modal.style.height = `${newHeight}px`;
            });
        };

        const handleMouseUp = () => {
            if (!resizeState.current.isResizing) return;
            
            // Get final dimensions from DOM
            const computedStyle = window.getComputedStyle(modal);
            const finalWidth = parseInt(computedStyle.width) - (isDaireOpen ? sidebarWidth : 0);
            const finalHeight = parseInt(computedStyle.height);
            
            // Update state with final dimensions
            setModalDimensions({ width: finalWidth, height: finalHeight });
            
            // Clean up
            resizeState.current.isResizing = false;
            resizeState.current.direction = '';
            setIsResizing(false);
            
            // Restore normal behavior
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.body.style.pointerEvents = '';
            modal.style.pointerEvents = '';
            
            // Remove listeners
            document.removeEventListener('mousemove', handleMouseMove, true);
            document.removeEventListener('mouseup', handleMouseUp, true);
        };

        // Use capture phase to ensure we get the events
        document.addEventListener('mousemove', handleMouseMove, true);
        document.addEventListener('mouseup', handleMouseUp, true);
    };

    // Position modal at current cursor/viewport position - only on initial show
    // Commented out to allow CSS flexbox centering to work properly
    /*
    useEffect(() => {
        if (show && modalRef.current && !isFullScreen && !hasBeenMoved) {
            const modalElement = modalRef.current;
            
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            
            // Get modal dimensions (without sidebar for initial positioning)
            const modalWidth = dimensions.width;
            const modalHeight = dimensions.height;
            
            // Calculate ideal position (center of current viewport - no scroll compensation)
            let idealX = (viewportWidth - modalWidth) / 2;
            let idealY = (viewportHeight - modalHeight) / 2;
            
            // Ensure modal stays within viewport bounds with margin
            const margin = 20;
            idealX = Math.max(margin, Math.min(idealX, viewportWidth - modalWidth - margin));
            idealY = Math.max(margin, Math.min(idealY, viewportHeight - modalHeight - margin));
            
            // Apply position using fixed positioning to avoid scroll issues
            modalElement.style.position = 'fixed';
            modalElement.style.left = `${idealX}px`;
            modalElement.style.top = `${idealY}px`;
            modalElement.style.transform = 'none';
            
            // Store position for draggable
            setCursorPosition({ x: idealX, y: idealY });
        }
    }, [show, isFullScreen]); // Removed dimensions, isDaireOpen, sidebarWidth from dependencies
    */

    const fetchPdf = async () => {
        setIsLoading(true);
        setError(null);

        try {

            //Simplified the logic for external URLs
            if (externalUrls) {
                setPdfUrl(url);
                setIsLoading(false);
                return;
            }

            // Only try title-based fetching if listItemId failed or isn't available
            if (url) {
                const cleanUrl = url.replace("direct-", "");
                try {
                    const response = await apiHelper.fetchPDFNameAndContentByTitle(extractFileName(cleanUrl));
                    if (response?.fileContent) {
                        const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
                        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                        setPdfUrl(pdfBlobUrl);
                        setIsLoading(false);
                        return;
                    }
                } catch (err) {
                    console.warn('Failed to fetch PDF by title, trying listItemId:', err);
                }
            }

            // Try listItemId first if available
            if (listItemId) {
                try {
                    const response = await apiHelper.fetchPDFNameAndContentById(listItemId);
                    if (response?.fileContent) {
                        const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
                        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                        setPdfUrl(pdfBlobUrl);
                        setIsLoading(false);
                        return;
                    }
                } catch (err) {
                    console.warn('Failed to fetch PDF by listItemId, trying URL:', err);
                }
            }



            // If both methods fail, try using the URL directly
            if (url) {
                setPdfUrl(url);
            } else {
                throw new Error('PDF not found');
            }
        } catch (err) {
            console.error('Failed to load PDF:', err);
            setError('Failed to load PDF');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPdf();
    }, [listItemId, title, url, show]);

    // Log when modal opens
    useEffect(() => {
        if (show && title && user?.profile?.email) {
            try {
                apiHelper.logModalOpen(user.profile.email, 'PDF Document', title);
            } catch (error) {
                // Silent fail - don't disrupt user experience
            }
        }
    }, [show, title, user?.profile?.email]);

    // Cleanup resize listeners on unmount
    useEffect(() => {
        return () => {
            if (resizeState.current.isResizing) {
                document.body.style.userSelect = '';
                document.body.style.webkitUserSelect = '';
                document.body.style.pointerEvents = '';
                // Note: We can't remove specific listeners here since we don't have references
                // but the component unmounting should handle this
            }
        };
    }, []);

    // Listen for browser fullscreen changes (ESC key, F11, etc.)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            
            if (isFullScreen && !isCurrentlyFullscreen) {
                // User exited fullscreen via ESC or other means
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, [isFullScreen]);

    // Handle style conflicts between resize and fullscreen modes
    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;

        if (isFullScreen) {
            // Store current styles for restoration
            const currentStyles = {
                width: modal.style.width,
                height: modal.style.height,
                top: modal.style.top,
                left: modal.style.left,
                transform: modal.style.transform,
                position: modal.style.position
            };
            
            // Clear resize styles that interfere with fullscreen
            modal.style.width = '';
            modal.style.height = '';
            modal.style.top = '';
            modal.style.left = '';
            modal.style.transform = '';
            modal.style.position = '';
            
            // Store styles for restoration
            modal.dataset.storedStyles = JSON.stringify(currentStyles);
        } else {
            // Restore windowed mode styles
            if (modal.dataset.storedStyles) {
                try {
                    const storedStyles = JSON.parse(modal.dataset.storedStyles);
                    // Only restore if they were actual resize styles
                    if (storedStyles.width && storedStyles.height) {
                        modal.style.width = storedStyles.width;
                        modal.style.height = storedStyles.height;
                    } else {
                        // Fallback to calculated dimensions
                        const totalWidth = modalDimensions.width + (isDaireOpen ? sidebarWidth : 0);
                        modal.style.width = `${totalWidth}px`;
                        modal.style.height = `${modalDimensions.height}px`;
                    }
                    modal.style.top = storedStyles.top || '';
                    modal.style.left = storedStyles.left || '';
                    modal.style.transform = storedStyles.transform || '';
                    modal.style.position = storedStyles.position || '';
                } catch (e) {
                    // Fallback to calculated dimensions
                    const totalWidth = modalDimensions.width + (isDaireOpen ? sidebarWidth : 0);
                    modal.style.width = `${totalWidth}px`;
                    modal.style.height = `${modalDimensions.height}px`;
                }
                delete modal.dataset.storedStyles;
            }
        }
    }, [isFullScreen, modalDimensions, isDaireOpen, sidebarWidth]);

    // Force scrolling for dynamic content
    useEffect(() => {
        if (isDaireOpen) {
            const style = document.createElement('style');
            style.id = 'pdf-modal-force-scroll';
            style.textContent = `
                /* Remove all default spacing from DaireChat container */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content {
                    padding: 0 !important;
                    margin: 0 !important;
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat {
                    padding: 0 !important;
                    margin: 0 !important;
                    background: white !important;
                    border-radius: 0 !important;
                    box-shadow: none !important;
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                    justify-content: stretch !important;
                }
                
                /*  Override everything */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window {
                    min-height: 0 !important;
                    height: calc(100vh - 120px) !important;
                    max-height: calc(100vh - 120px) !important;
                    flex: 1 !important;
                    overflow-y: auto !important;
                    overflow-x: hidden !important;
                    padding: 1rem !important;
                    margin: 0 !important;
                    position: relative !important;
                }
                
                /* Windowed mode - constrained height */
                .pdf-modal.with-sidebar:not(.fullscreen) .pdf-daire-sidebar .pdf-daire-content .daira-chat {
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                    overflow: hidden !important;
                }
                
                .pdf-modal.with-sidebar:not(.fullscreen) .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window {
                    height: calc(100vh - 200px) !important;
                    max-height: calc(100vh - 200px) !important;
                    min-height: 0 !important;
                }
                
                .pdf-modal.with-sidebar:not(.fullscreen) .pdf-daire-sidebar .pdf-daire-content .daira-chat .input-form {
                    flex-shrink: 0 !important;
                    padding: 0.5rem !important;
                    margin: 0 !important;
                    background: #f8fafc !important;
                    border-top: 1px solid #e2e8f0 !important;
                    height: 60px !important;
                    min-height: 60px !important;
                    max-height: 60px !important;
                }
                
                .pdf-modal.with-sidebar:not(.fullscreen) .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-toolbar {
                    flex-shrink: 0 !important;
                    padding: 0.5rem !important;
                    margin: 0 !important;
                    background: var(--modal-header-color) !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                    height: 50px !important;
                    min-height: 50px !important;
                    max-height: 50px !important;
                }
                
                /* Remove any container padding/margins that create white space */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat > div {
                    margin: 0 !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-container,
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-wrapper,
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .main-container {
                    padding: 0 !important;
                    margin: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                }
                
                /* Force DaireChat to stick to edges - no centering */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat {
                    position: relative !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    transform: none !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    min-height: 100% !important;
                    max-height: 100% !important;
                }
                
                /* Minimize disclaimer in PDF sidebar */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .ai-disclaimer {
                    padding: 0.25rem 0.5rem !important;
                    margin: 0 !important;
                    font-size: 0.7rem !important;
                    line-height: 1.2 !important;
                    min-height: auto !important;
                    flex-shrink: 0 !important;
                    background: #f8fafc !important;
                    border-top: 1px solid #e2e8f0 !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .disclaimer-collapsed {
                    padding: 0.25rem !important;
                    margin: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 0.25rem !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .disclaimer-icon {
                    font-size: 0.7rem !important;
                    margin: 0 !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .disclaimer-text {
                    font-size: 0.65rem !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .disclaimer-expand-button {
                    font-size: 0.6rem !important;
                    padding: 0.1rem !important;
                    margin: 0 !important;
                }
                
                /* Custom scrollbar styling for both modes */
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window::-webkit-scrollbar {
                    width: 8px !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window::-webkit-scrollbar-track {
                    background: #f1f1f1 !important;
                    border-radius: 4px !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window::-webkit-scrollbar-thumb {
                    background: #00b140 !important;
                    border-radius: 4px !important;
                    transition: background 0.2s ease !important;
                }
                
                .pdf-modal.with-sidebar .pdf-daire-sidebar .pdf-daire-content .daira-chat .chat-window::-webkit-scrollbar-thumb:hover {
                    background: #009935 !important;
                }
            `;
            document.head.appendChild(style);
            
            return () => {
                const existingStyle = document.getElementById('pdf-modal-force-scroll');
                if (existingStyle) {
                    existingStyle.remove();
                }
            };
        }
    }, [isDaireOpen]);

    // ESC key listener for fullscreen mode
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'Escape' && isFullScreen) {
                setIsFullScreen(false);
            }
        };

        if (isFullScreen) {
            document.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [isFullScreen]);

    const toggleFullScreen = () => {
        if (!isFullScreen) {
            // First set the state to trigger re-render with fullscreen layout
            setIsFullScreen(true);
            
            // Then request fullscreen on the document body or a reliable element
            setTimeout(() => {
                const targetElement = document.querySelector('.pdf-modal-overlay.fullscreen') || document.body;
                if (targetElement && targetElement.requestFullscreen) {
                    targetElement.requestFullscreen().catch(err => {
                        console.warn('PDF Modal fullscreen entry failed:', err);
                    });
                } else if (targetElement && targetElement.webkitRequestFullscreen) {
                    targetElement.webkitRequestFullscreen();
                } else if (targetElement && targetElement.msRequestFullscreen) {
                    targetElement.msRequestFullscreen();
                }
            }, 50);
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(err => {
                    console.warn('PDF Modal fullscreen exit failed:', err);
                });
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullScreen(false);
        }
    };

    const appliedStyle = isFullScreen
        ? { zIndex: style?.zIndex ?? 1000 }
        : { ...style, zIndex: style?.zIndex ?? 1000 };

    if (!show) return null;

    const openPdfInNewTab = () => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
        }
    };

    // Get CSS color class for consultation category
    const getCategoryColor = (category) => {
        const colorMap = {
            'Consultation': 'consultation',
            'Paper': 'paper',
            'Submission': 'submission',
            'Response': 'coral',
            'Statement': 'dust-rose',
            'Guidance': 'cyan',
            'Policy': 'peach',
            'Regulation': 'sage',
            'Directive': 'purple',
            'Technical': 'teal',
            'Legal': 'indigo'
        };
        
        const normalizedCategory = category?.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            key.toLowerCase() === normalizedCategory || 
            normalizedCategory?.includes(key.toLowerCase())
        );
        
        return colorMap[matchedKey] || 'light-gray';
    };

    const documentContext = {
        isDocumentContext: true,
        title: title || '',
        listItemId: listItemId ? Number(listItemId) : null,
        url: url
    };

    // Render fullscreen modal separately to break out of parent constraints
    if (isFullScreen) {
        return (
            <div className="pdf-modal-overlay fullscreen">
                <div
                    ref={modalRef}
                    className={`pdf-modal fullscreen ${isDaireOpen ? 'with-sidebar' : ''}`}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 9999,
                        margin: 0,
                        borderRadius: 0
                    }}
                    onClick={onBringToFront}
                >
                    <div className="pdf-modal-main">
                        <div className="modal-content">
                            <div className="modal-header">
                                <Tippy content={title}>
                                    <div className="modalTitle">{title}</div>
                                </Tippy>
                                <div className="toolbar-right">
                                    <span
                                        title="Ask Daire about this document"
                                        className={`pointer header-icon daire-header-icon ${isDaireOpen ? 'active' : ''}`}
                                        onClick={() => setIsDaireOpen(!isDaireOpen)}
                                    >
                                                                <img
                            src="/assets/img/daire/daire-logo-icon.png"
                            alt="Daire Logo"
                            className="daire-header-icon-img"
                        />
                    </span>
                    {consultationData && (
                        <span
                            title={showConsultationDetails ? "Hide consultation details" : "Show consultation details"}
                            className={`pointer header-icon consultation-details-icon ${showConsultationDetails ? 'active' : ''}`}
                            onClick={() => setShowConsultationDetails(!showConsultationDetails)}
                        >
                            <i className="fas fa-info-circle"></i>
                        </span>
                    )}
                    {showLinkinToolBar && (
                        <a className="link-btn header-icon" title="Open PDF in a new window" onClick={openPdfInNewTab} style={{ cursor: 'pointer' }}>
                            <i className="fa-solid fa-link"></i>
                        </a>
                    )}
                    {allowFullscreen && (
                        <span
                            title={isFullScreen ? "Compress" : "Expand"}
                            className="pdf-modal-fullscreen-toggle pdf-fullscreen-btn pdf-header-icon"
                            onClick={toggleFullScreen}
                            style={{ cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
                        </span>
                    )}
                    <span
                        title="Close"
                        className="pointer fas fa-times header-icon"
                        onClick={onClose}
                    ></span>
                </div>
            </div>
            
            {/* Consultation Details Section - Fullscreen */}
            <AnimatePresence>
                {consultationData && showConsultationDetails && (
                    <motion.div
                        className="consultation-details-section"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                            height: { duration: 0.3, ease: 'easeInOut' },
                            opacity: { duration: 0.2, ease: 'easeInOut' }
                        }}
                        style={{ overflow: 'hidden' }}
                    >
                        <motion.div
                            className="consultation-details-content"
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ delay: 0.1, duration: 0.2, ease: 'easeOut' }}
                        >
                            <div className="consultation-details-header">
                                <h4>Consultation Details</h4>
                            </div>
                            <div className="consultation-details-body">
                                {consultationData.topic && consultationData.topic !== 'N/A' && (
                                    <motion.div 
                                        className="consultation-detail-item"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05, duration: 0.25, ease: 'easeOut' }}
                                    >
                                        <label>Description:</label>
                                        <p>{normalizeText(consultationData.topic)}</p>
                                    </motion.div>
                                )}
                                
                                <motion.div 
                                    className="consultation-details-grid"
                                    initial="hidden"
                                    animate="visible"
                                    variants={{
                                        hidden: { opacity: 0 },
                                        visible: {
                                            opacity: 1,
                                            transition: {
                                                staggerChildren: 0.05,
                                                delayChildren: 0.15
                                            }
                                        }
                                    }}
                                >
                                    {consultationData.status && (
                                        <motion.div 
                                            className="consultation-detail-item"
                                            variants={{
                                                hidden: { opacity: 0, y: 10 },
                                                visible: { opacity: 1, y: 0 }
                                            }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <label>Status:</label>
                                            <span className={`consultation-status-badge ${consultationData.status.toLowerCase()}`}>
                                                {consultationData.status}
                                            </span>
                                        </motion.div>
                                    )}
                                    
                                    {consultationData.category && (
                                        <motion.div 
                                            className="consultation-detail-item"
                                            variants={{
                                                hidden: { opacity: 0, y: 10 },
                                                visible: { opacity: 1, y: 0 }
                                            }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <label>Category:</label>
                                            <span className={`consultation-category-tag consultation-tag-${getCategoryColor(consultationData.category)}`}>
                                                {consultationData.category}
                                            </span>
                                        </motion.div>
                                    )}
                                    
                                    {consultationData.completed && consultationData.completed !== 'No Date Available' && (
                                        <motion.div 
                                            className="consultation-detail-item"
                                            variants={{
                                                hidden: { opacity: 0, y: 10 },
                                                visible: { opacity: 1, y: 0 }
                                            }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <label>Completed:</label>
                                            <span className="consultation-date">{formatDate(consultationData.completed)}</span>
                                        </motion.div>
                                    )}
                                    
                                    {consultationData.wg && consultationData.wg !== 'N/A' && (
                                        <motion.div 
                                            className="consultation-detail-item"
                                            variants={{
                                                hidden: { opacity: 0, y: 10 },
                                                visible: { opacity: 1, y: 0 }
                                            }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <label>Working Group:</label>
                                            <span className="consultation-working-group">{consultationData.wg}</span>
                                        </motion.div>
                                    )}
                                    
                                    {consultationData.ib && consultationData.ib !== 'N/A' && (
                                        <motion.div 
                                            className="consultation-detail-item"
                                            variants={{
                                                hidden: { opacity: 0, y: 10 },
                                                visible: { opacity: 1, y: 0 }
                                            }}
                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <label>Issuing Body:</label>
                                            <span className="consultation-issuing-body">{consultationData.ib}</span>
                                        </motion.div>
                                    )}
                                </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            
            <div className="modal-body-container">
                <div className="modal-body">
                    {isLoading ? (
                        <>
                        <div style={{ marginTop: '100px' }}>
                            <LoadingSpinner text="Loading PDF" />
                        </div>
                        </>
                    ) : error ? (
                        <p className="error-text">{error}</p>
                    ) : (
                        <iframe src={pdfUrl} title={title} />
                    )}
                </div>
            </div>
        </div>
    </div>

    {/* Integrated Daire Sidebar - Fullscreen */}
                    <AnimatePresence>
                        {isDaireOpen && (
                            <motion.div
                                className="pdf-daire-sidebar"
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 30
                                }}
                                style={{ width: `${sidebarWidth}px` }}
                            >
                                <div className="pdf-daire-content">
                                    <DaireChat 
                                        showQuickActions={false}
                                        documentContext={documentContext}
                                        onNavigateHome={() => setIsDaireOpen(false)}
                                        showWelcome={false}
                                        isSidebar={true}
                                        showDocumentSuggestions={true}
                                        hideFullscreen={true}
                                    />
                                </div>

                                <div
                                    className={`pdf-daire-resize-handle ${isDraggingSidebar ? 'dragging' : ''}`}
                                    onMouseDown={handleSidebarResize}
                                />

                                <Tippy content="Close Daire">
                                    <button 
                                        className="pdf-daire-close-btn"
                                        onClick={() => setIsDaireOpen(false)}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </Tippy>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        );
    }

    // Normal windowed mode
    return (
        <div className="pdf-modal-overlay">
            <Draggable
                handle=".modal-header"
                disabled={!allowMove || isResizing || resizeState.current.isResizing}
                nodeRef={modalRef}
                defaultPosition={{ x: 0, y: 0 }}
                onStart={() => {
                    if (resizeState.current.isResizing) return false;
                    setHasBeenMoved(true);
                }}
            >
                <div
                    ref={modalRef}
                    className={`pdf-modal ${!allowMove ? 'nomove' : ''} ${isDaireOpen ? 'with-sidebar' : ''} ${isResizing ? 'resizing' : ''}`}
                    style={{
                        ...appliedStyle,
                        width: `${modalDimensions.width + (isDaireOpen ? sidebarWidth : 0)}px`,
                        height: `${modalDimensions.height}px`
                    }}
                    onClick={onBringToFront}
                >
                    <div className="pdf-modal-main">
                        <div className="modal-content">
                            <div className="modal-header">
                                <Tippy content={title}>
                                    <div className="modalTitle">{title}</div>
                                </Tippy>
                                <div className="toolbar-right">
                                    <span
                                        title="Ask Daire about this document"
                                        className={`pointer header-icon daire-header-icon ${isDaireOpen ? 'active' : ''}`}
                                        onClick={() => setIsDaireOpen(!isDaireOpen)}
                                    >
                                        <img
                                            src="/assets/img/daire/daire-logo-icon.png"
                                            alt="Daire Logo"
                                            className="daire-header-icon-img"
                                            
                                        />
                                    </span>
                                    {consultationData && (
                                        <span
                                            title={showConsultationDetails ? "Hide consultation details" : "Show consultation details"}
                                            className={`pointer header-icon consultation-details-icon ${showConsultationDetails ? 'active' : ''}`}
                                            onClick={() => setShowConsultationDetails(!showConsultationDetails)}
                                        >
                                            <i className="fas fa-info-circle"></i>
                                        </span>
                                    )}
                                    {showLinkinToolBar && (
                                        <a className="link-btn header-icon" title="Open PDF in a new window" onClick={openPdfInNewTab} style={{ cursor: 'pointer' }}>
                                            <i className="fa-solid fa-link"></i>
                                        </a>
                                    )}
                                    {allowFullscreen && (
                                        <span
                                            title={isFullScreen ? "Compress" : "Expand"}
                                            className="pdf-modal-fullscreen-toggle pdf-fullscreen-btn pdf-header-icon"
                                            onClick={toggleFullScreen}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <FontAwesomeIcon icon={isFullScreen ? faCompress : faExpand} />
                                        </span>
                                    )}
                                    <span
                                        title="Close"
                                        className="pointer fas fa-times header-icon"
                                        onClick={onClose}
                                    ></span>
                                </div>
                            </div>
                            
                            {/* Consultation Details Section - Windowed */}
                            <AnimatePresence>
                                {consultationData && showConsultationDetails && (
                                    <motion.div
                                        className="consultation-details-section"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{
                                            height: { duration: 0.3, ease: 'easeInOut' },
                                            opacity: { duration: 0.2, ease: 'easeInOut' }
                                        }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <motion.div
                                            className="consultation-details-content"
                                            initial={{ y: -20, opacity: 0 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            exit={{ y: -20, opacity: 0 }}
                                            transition={{ delay: 0.1, duration: 0.2, ease: 'easeOut' }}
                                        >
                                            <div className="consultation-details-header">
                                                <h4>Consultation Details</h4>
                                            </div>
                                            <div className="consultation-details-body">
                                                                                            {consultationData.topic && consultationData.topic !== 'N/A' && (
                                                <motion.div 
                                                    className="consultation-detail-item"
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.05, duration: 0.25, ease: 'easeOut' }}
                                                >
                                                    <label>Description:</label>
                                                    <p>{normalizeText(consultationData.topic)}</p>
                                                </motion.div>
                                            )}
                                                
                                                <motion.div 
                                                    className="consultation-details-grid"
                                                    initial="hidden"
                                                    animate="visible"
                                                    variants={{
                                                        hidden: { opacity: 0 },
                                                        visible: {
                                                            opacity: 1,
                                                            transition: {
                                                                staggerChildren: 0.05,
                                                                delayChildren: 0.15
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {consultationData.status && (
                                                        <motion.div 
                                                            className="consultation-detail-item"
                                                            variants={{
                                                                hidden: { opacity: 0, y: 10 },
                                                                visible: { opacity: 1, y: 0 }
                                                            }}
                                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                                        >
                                                            <label>Status:</label>
                                                            <span className={`consultation-status-badge ${consultationData.status.toLowerCase()}`}>
                                                                {consultationData.status}
                                                            </span>
                                                        </motion.div>
                                                    )}
                                                    
                                                    {consultationData.category && (
                                                        <motion.div 
                                                            className="consultation-detail-item"
                                                            variants={{
                                                                hidden: { opacity: 0, y: 10 },
                                                                visible: { opacity: 1, y: 0 }
                                                            }}
                                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                                        >
                                                            <label>Category:</label>
                                                            <span className={`consultation-category-tag consultation-tag-${getCategoryColor(consultationData.category)}`}>
                                                                {consultationData.category}
                                                            </span>
                                                        </motion.div>
                                                    )}
                                                    
                                                    {consultationData.completed && consultationData.completed !== 'No Date Available' && (
                                                        <motion.div 
                                                            className="consultation-detail-item"
                                                            variants={{
                                                                hidden: { opacity: 0, y: 10 },
                                                                visible: { opacity: 1, y: 0 }
                                                            }}
                                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                                        >
                                                            <label>Completed:</label>
                                                            <span className="consultation-date">{formatDate(consultationData.completed)}</span>
                                                        </motion.div>
                                                    )}
                                                    
                                                    {consultationData.wg && consultationData.wg !== 'N/A' && (
                                                        <motion.div 
                                                            className="consultation-detail-item"
                                                            variants={{
                                                                hidden: { opacity: 0, y: 10 },
                                                                visible: { opacity: 1, y: 0 }
                                                            }}
                                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                                        >
                                                            <label>Working Group:</label>
                                                            <span className="consultation-working-group">{consultationData.wg}</span>
                                                        </motion.div>
                                                    )}
                                                    
                                                    {consultationData.ib && consultationData.ib !== 'N/A' && (
                                                        <motion.div 
                                                            className="consultation-detail-item"
                                                            variants={{
                                                                hidden: { opacity: 0, y: 10 },
                                                                visible: { opacity: 1, y: 0 }
                                                            }}
                                                            transition={{ duration: 0.2, ease: 'easeOut' }}
                                                        >
                                                            <label>Issuing Body:</label>
                                                            <span className="consultation-issuing-body">{consultationData.ib}</span>
                                                        </motion.div>
                                                    )}
                                                </motion.div>
                                            </div>
                                        </motion.div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            
                            <div className="modal-body-container">
                                <div className="modal-body">
                                    {isLoading ? (
                                        <>
                                        <div style={{ marginTop: '100px' }}>
                                            <LoadingSpinner text="Loading PDF" />
                                        </div>
                                        </>
                                    ) : error ? (
                                        <p className="error-text">{error}</p>
                                    ) : (
                                        <iframe src={pdfUrl} title={title} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Integrated Daire Sidebar - Windowed */}
                    <AnimatePresence>
                        {isDaireOpen && (
                            <motion.div
                                className="pdf-daire-sidebar"
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 30
                                }}
                                style={{ width: `${sidebarWidth}px` }}
                            >
                                <div className="pdf-daire-content">
                                    <DaireChat 
                                        showQuickActions={false}
                                        documentContext={documentContext}
                                        onNavigateHome={() => setIsDaireOpen(false)}
                                        showWelcome={false}
                                        isSidebar={true}
                                        showDocumentSuggestions={true}
                                        hideFullscreen={true}
                                    />
                                </div>

                                <div
                                    className={`pdf-daire-resize-handle ${isDraggingSidebar ? 'dragging' : ''}`}
                                    onMouseDown={handleSidebarResize}
                                />

                                <Tippy content="Close Daire">
                                    <button 
                                        className="pdf-daire-close-btn"
                                        onClick={() => setIsDaireOpen(false)}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </Tippy>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Custom Resize Handles */}
                    {!isFullScreen && (
                        <>
                            {/* Corner handles */}
                            <div className="resize-handle resize-se" onMouseDown={handleResizeStart('se')} />
                            <div className="resize-handle resize-sw" onMouseDown={handleResizeStart('sw')} />
                            <div className="resize-handle resize-ne" onMouseDown={handleResizeStart('ne')} />
                            <div className="resize-handle resize-nw" onMouseDown={handleResizeStart('nw')} />
                            
                            {/* Edge handles */}
                            <div className="resize-handle resize-n" onMouseDown={handleResizeStart('n')} />
                            <div className="resize-handle resize-s" onMouseDown={handleResizeStart('s')} />
                            <div className="resize-handle resize-e" onMouseDown={handleResizeStart('e')} />
                            <div className="resize-handle resize-w" onMouseDown={handleResizeStart('w')} />
                        </>
                    )}
                </div>
            </Draggable>
        </div>
    );
}

export default PDFModal;
